import { Directive, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { Subscription } from 'rxjs';
import { RxEmitter } from 'common/rxjs/rx-emitter';

@Directive({
  selector: '[appTraversableList]'
})
export class TraversableListDirective implements OnInit, OnDestroy {
  private static readonly TraversableItemClassName = 'traversable-list-item';
  private static readonly TraversableItemSelector = `.${TraversableListDirective.TraversableItemClassName}`;

  private _lastFocusedItem?: HTMLElement;
  private _traverseDownSubscription: Subscription;
  private _traverseUpSubscription: Subscription;

  constructor(private _element: ElementRef, private _rxEmitter: RxEmitter) {
  }

  ngOnInit(): void {
    this._traverseDownSubscription = this._rxEmitter.getNamespaceObservable(MessageBusEvents.TraverseWorkItemUp)
      .subscribe(() => {
        this.focusPreviousItem();
      });
    this._traverseUpSubscription = this._rxEmitter.getNamespaceObservable(MessageBusEvents.TraverseWorkItemDown)
      .subscribe(() => {
        this.focusNextItem();
      });
  }

  @HostListener('focusin', ['$event'])
  onFocusChangedIn(ev: FocusEvent): void {
    const target: HTMLElement = ev.target as unknown as HTMLElement;
    if (target?.classList.contains(TraversableListDirective.TraversableItemClassName)) {
      this._lastFocusedItem = target;
    }
  }

  ngOnDestroy(): void {
    this._traverseDownSubscription?.unsubscribe();
    this._traverseUpSubscription?.unsubscribe();
  }

  private focusNextItem(): void {
    const allItems = this._getAllItems();

    let itemToSelect: HTMLElement;

    if (!this._lastFocusedItem) {
      itemToSelect = allItems[0];
    } else {
      const lastFocusedItemIndex = allItems.indexOf(this._lastFocusedItem);

      const indexToSelect = (lastFocusedItemIndex < allItems.length - 1) ? (lastFocusedItemIndex + 1) : allItems.length - 1;

      itemToSelect = allItems[indexToSelect];
    }

    this._focusItem(itemToSelect);
  }

  private focusPreviousItem(): void {
    const allItems = this._getAllItems();

    let itemToSelect: HTMLElement;

    if (!this._lastFocusedItem) {
      itemToSelect = allItems[0];
    } else {
      const lastFocusedItemIndex = allItems.indexOf(this._lastFocusedItem);

      const indexToSelect = (lastFocusedItemIndex <= 0) ? 0 : (lastFocusedItemIndex - 1);

      itemToSelect = allItems[indexToSelect];
    }

    this._focusItem(itemToSelect);
  }

  private _getAllItems(): HTMLElement[] {
    return Array.from(this._element.nativeElement.querySelectorAll(TraversableListDirective.TraversableItemSelector));
  }

  private _focusItem(item: HTMLElement): void {
    if (item) {
      // Make the item programmatically focusable
      if (!item.hasAttribute('tabindex')) {
        item.setAttribute('tabindex', '-1');
      }
      item.focus();
    }

    this._lastFocusedItem = item;
  }
}
